import { isDataVizToneName, type DataVizToneName } from "../../theme/dataVizPalette";
import type {
  TimeSeries as MediaTimelineSeries,
  TimeSeriesDomain as MediaTimelineSeriesDomain,
  TimeSeriesInterpolation as MediaTimelineSeriesInterpolation,
  TimeSeriesLineStyle,
} from "./seriesTypes";

const interpolations = { linear: true, step: true } satisfies Record<
  MediaTimelineSeriesInterpolation,
  true
>;
const lineStyles = { solid: true, dashed: true } satisfies Record<TimeSeriesLineStyle, true>;

export function isSeriesTone(value: unknown): value is DataVizToneName {
  return typeof value === "string" && isDataVizToneName(value);
}

function isInterpolation(value: unknown): value is MediaTimelineSeriesInterpolation {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(interpolations, value);
}

function isLineStyle(value: unknown): value is TimeSeriesLineStyle {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(lineStyles, value);
}

const isHold = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** Display form of one series: parallel typed arrays in ascending time, `NaN` values as gaps. */
export interface NormalizedSeries {
  id: string;
  /** `undefined` is the ink line. */
  tone: DataVizToneName | undefined;
  interpolation: MediaTimelineSeriesInterpolation;
  lineStyle: TimeSeriesLineStyle;
  /** Trailing hold of the last sample; `0` ends the line at the sample. */
  holdSeconds: number;
  label?: string;
  times: Float64Array;
  values: Float64Array;
}

/** One band of a lane-stacked plot. Lanes share the plot's value axis unless one declares its own. */
export interface SeriesLane {
  id: string;
  label?: string;
  series: readonly NormalizedSeries[];
  /** This lane's own value axis; omitted means the plot's shared axis. */
  domain?: MediaTimelineSeriesDomain;
}

/** A lane with its axis settled: the shared one, or the lane's own when it declared one. */
export interface SeriesResolvedLane extends SeriesLane {
  domain: MediaTimelineSeriesDomain;
  /** True when `domain` is the lane's own rather than the shared axis; the readout says so. */
  ownDomain: boolean;
}

export type MediaTimelineSeriesIssueCode =
  | "invalid-series"
  | "invalid-id"
  | "invalid-tone"
  | "invalid-interpolation"
  | "invalid-line-style"
  | "invalid-hold"
  | "invalid-points"
  | "non-finite-time"
  | "unsorted-time"
  | "empty-series";

export interface MediaTimelineSeriesValidationIssue {
  /** Index in the original input, including rejected entries. */
  index: number;
  code: MediaTimelineSeriesIssueCode;
}

/**
 * Read-only diagnostics for API/consumer data. Does not repair, filter or warn. A non-finite
 * value is a gap by contract, so it is never reported; a non-finite time cannot be placed and is.
 */
export function validateMediaTimelineSeries(
  series: readonly unknown[],
): MediaTimelineSeriesValidationIssue[] {
  const issues: MediaTimelineSeriesValidationIssue[] = [];
  series.forEach((entry, index) => {
    const report = (code: MediaTimelineSeriesIssueCode) => issues.push({ index, code });
    if (!isRecord(entry)) {
      report("invalid-series");
      return;
    }
    if (typeof entry.id !== "string") report("invalid-id");
    if (entry.tone !== undefined && !isSeriesTone(entry.tone)) report("invalid-tone");
    if (entry.interpolation !== undefined && !isInterpolation(entry.interpolation)) {
      report("invalid-interpolation");
    }
    if (entry.lineStyle !== undefined && !isLineStyle(entry.lineStyle))
      report("invalid-line-style");
    if (entry.holdSeconds !== undefined && !isHold(entry.holdSeconds)) report("invalid-hold");
    if (!Array.isArray(entry.points)) {
      report("invalid-points");
      return;
    }
    let placeable = 0;
    let previous = -Infinity;
    let unsorted = false;
    let nonFinite = false;
    for (const point of entry.points as readonly unknown[]) {
      const time = isRecord(point) ? point.timeSeconds : undefined;
      if (typeof time !== "number" || !Number.isFinite(time)) {
        nonFinite = true;
        continue;
      }
      placeable += 1;
      if (time < previous) unsorted = true;
      previous = time;
    }
    if (nonFinite) report("non-finite-time");
    if (unsorted) report("unsorted-time");
    if (placeable === 0) report("empty-series");
  });
  return issues;
}

export function isSeriesDomain(value: unknown): value is MediaTimelineSeriesDomain {
  return (
    isRecord(value) &&
    typeof value.min === "number" &&
    typeof value.max === "number" &&
    Number.isFinite(value.min) &&
    Number.isFinite(value.max) &&
    value.min < value.max
  );
}

/**
 * Display-only copies: samples that cannot be placed are dropped, non-finite values become gaps,
 * an unsorted input is ordered (stable), and a series with nothing to place is omitted. Never
 * mutates consumer data. Unlike bands, samples are not clamped to the media: a sample just past
 * the duration ends the line where the data ends instead of a step early.
 */
export function normalizeSeries(
  series: readonly MediaTimelineSeries[] | undefined,
): readonly NormalizedSeries[] {
  if (!series) return [];
  const out: NormalizedSeries[] = [];
  for (const entry of series) {
    if (
      !isRecord(entry) ||
      typeof entry.id !== "string" ||
      (entry.tone !== undefined && !isSeriesTone(entry.tone)) ||
      !Array.isArray(entry.points)
    )
      continue;
    const points = entry.points as readonly unknown[];
    const times: number[] = [];
    const values: number[] = [];
    let sorted = true;
    for (const point of points) {
      if (!isRecord(point)) continue;
      const time = point.timeSeconds;
      if (typeof time !== "number" || !Number.isFinite(time)) continue;
      const value = point.value;
      if (sorted && times.length > 0 && time < times[times.length - 1]!) sorted = false;
      times.push(time);
      values.push(typeof value === "number" && Number.isFinite(value) ? value : Number.NaN);
    }
    if (times.length === 0) continue;
    let order: number[] | undefined;
    if (!sorted) {
      order = times.map((_, index) => index).sort((a, b) => times[a]! - times[b]! || a - b);
    }
    const normalized: NormalizedSeries = {
      id: entry.id,
      tone: entry.tone as DataVizToneName | undefined,
      interpolation: isInterpolation(entry.interpolation) ? entry.interpolation : "linear",
      lineStyle: isLineStyle(entry.lineStyle) ? entry.lineStyle : "solid",
      holdSeconds: isHold(entry.holdSeconds) ? entry.holdSeconds : 0,
      times: Float64Array.from(order ? order.map((index) => times[index]!) : times),
      values: Float64Array.from(order ? order.map((index) => values[index]!) : values),
    };
    if (typeof entry.label === "string") normalized.label = entry.label;
    out.push(normalized);
  }
  return out;
}

/** Extent of every finite value across the row, or `null` when nothing is finite. */
export function resolveSeriesExtent(
  series: readonly NormalizedSeries[],
): MediaTimelineSeriesDomain | null {
  let min = Infinity;
  let max = -Infinity;
  for (const entry of series) {
    for (const value of entry.values) {
      if (Number.isNaN(value)) continue;
      if (value < min) min = value;
      if (value > max) max = value;
    }
  }
  return min <= max ? { min, max } : null;
}

/** The authored axis when it is a valid range; otherwise the data extent (a flat line is allowed). */
export function resolveSeriesDomain(
  authored: unknown,
  series: readonly NormalizedSeries[],
): MediaTimelineSeriesDomain | null {
  if (isSeriesDomain(authored)) return { min: authored.min, max: authored.max };
  return resolveSeriesExtent(series);
}

const normalizedSources = new WeakMap<
  readonly MediaTimelineSeries[],
  readonly NormalizedSeries[]
>();

/** Immutable source identity owns prepared samples across views; weak keys retain no history. */
export function getNormalizedTimeSeries(
  source: readonly MediaTimelineSeries[],
): readonly NormalizedSeries[] {
  const cached = normalizedSources.get(source);
  if (cached) return cached;
  const normalized = normalizeSeries(source);
  normalizedSources.set(source, normalized);
  return normalized;
}
